import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationExtras, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormValidation } from '../../classes/formValidation';
import { COLORS } from '../../interfaces/constants';
import { UpdateService } from '../../services/update.service';
import { IRanch, RanchCreateResponse } from '../ranch-settings/interfaces';
import { RanchService } from '../ranch-settings/service';
import { Plantings } from '../plantings/plantings';
import { DIALOG_HIDE_CLASS } from '../shared/constants';
import { SharedKeepDiscardDialog } from '../shared/dialogs/keep-discard-dialog';
import { SharedUpdateService } from '../shared/dialogs/update.service';
import { RanchCreationPayload } from './ranchCreationPayload';
import { RanchCreation, RanchCreateStepFormKeys } from './interfaces';

interface RanchCreateStep {
	Id: RanchCreateStepFormKeys;
	Title: string;
}

@Component({
	moduleId: module.id,
	selector: 'ranch-create-dialog',
	templateUrl: 'ranch-create.html',
	styleUrls: ['ranch-create.scss']
})

export class RanchCreateDialog implements OnInit, OnDestroy {
	public COLORS = COLORS;
	public STEPS = RanchCreateStepFormKeys;
	public currentStepIndex = 0;
	public form: FormGroup;
	public isSaving = false;
	public steps: RanchCreateStep[] = [
		{ Id: RanchCreateStepFormKeys.RanchDetails, Title: 'Ranch Details' },
		{ Id: RanchCreateStepFormKeys.CommodityTypes, Title: 'Commodity Types' },
		{ Id: RanchCreateStepFormKeys.WeatherStations, Title: 'Weather Stations' },
		{ Id: RanchCreateStepFormKeys.Fertilizers, Title: 'Fertilizers' },
		{ Id: RanchCreateStepFormKeys.Wells, Title: 'Water Sources' },
		{ Id: RanchCreateStepFormKeys.PlantingAreas, Title: 'Planting Areas' }
	];

	public subscriptions$: Subject<boolean>;

	public get currentStep(): RanchCreateStep {
		return this.steps[this.currentStepIndex];
	}

	public get f(): { [key in RanchCreateStepFormKeys]: AbstractControl } {
		return this.form ? this.form.controls as { [key in RanchCreateStepFormKeys]: AbstractControl } : null;
	}

	public get ranchCoordinates(): string {
		return this.f && this.f.RanchDetails ? this.f.RanchDetails.get('Coordinates').value : null;
	}

	constructor(
		private _dialog: MatDialog,
		private _dialogRef: MatDialogRef<RanchCreateDialog>,
		private _fb: FormBuilder,
		private _ranchService: RanchService,
		private _router: Router,
		private _sharedUpdateService: SharedUpdateService,
		private _updateService: UpdateService
	) { }

	ngOnInit(): void {
		this.subscriptions$ = new Subject();
		this.form = this._initialize();
		this._bindKeepDiscardSubscriptions();

		this._dialogRef.backdropClick()
			.pipe(takeUntil(this.subscriptions$))
			.subscribe(() => {
				this.close();
			});
	}

	ngOnDestroy(): void {
		if (!this.subscriptions$) {
			return;
		}

		this.subscriptions$.next(true);
		this.subscriptions$.complete();
	}

	public back(): void {
		if (this.currentStepIndex === 0) {
			return;
		}

		this.currentStepIndex--;
	}

	public close(): void {
		if (this.form && this.form.dirty) {
			this._dialog.open(SharedKeepDiscardDialog, {
				width: '500px',
				disableClose: true,
				autoFocus: false
			});

			this._hide();
			return;
		}

		this._dialogRef.close();
	}

	public finish(): void {
		this.form.markAllAsTouched();
		this.form.updateValueAndValidity();

		if (!this.form.valid) {
			return;
		}

		this._create();
	}

	public nextStep(stepIndex: number): void {
		if (stepIndex < 0 || stepIndex >= this.steps.length) {
			return;
		}

		this.currentStepIndex = stepIndex;
	}

	public setRanchDetailsForm(form: FormGroup): void {
		this.form.setControl(RanchCreateStepFormKeys.RanchDetails, form);
	}

	private _initialize(): FormGroup {
		return this._fb.group({
			RanchDetails: this._fb.group({}),
			PlantingAreas: this._fb.array([]),
			WeatherStations: this._fb.array([], [FormValidation.formArrayMustNotBeEmpty()]),
			Wells: this._fb.array([]),
			Fertilizers: this._fb.array([], [FormValidation.formArrayMustNotBeEmpty()]),
			CommodityTypes: this._fb.array([], [FormValidation.formArrayMustNotBeEmpty()])
		});
	}

	private _bindKeepDiscardSubscriptions(): void {
		this._sharedUpdateService.keep$
			.pipe(takeUntil(this.subscriptions$))
			.subscribe(() => {
				this._show();
			});

		this._sharedUpdateService.discard$
			.pipe(takeUntil(this.subscriptions$))
			.subscribe(() => {
				this._dialogRef.close();
			});
	}

	private _hide(): void {
		this._dialogRef.addPanelClass(DIALOG_HIDE_CLASS);
	}

	private _show(): void {
		this._dialogRef.removePanelClass(DIALOG_HIDE_CLASS);
	}

	private _create(): void {
		let formValue: RanchCreation.Form.IParentForm;
		let payload: RanchCreation.IPayload;

		if (this.isSaving) {
			return;
		}

		formValue = this.form.value;
		payload = RanchCreationPayload.fromParentForm(formValue);
		this.isSaving = true;

		this._ranchService.createRanch(payload)
			.then((res) => {
				if (!res) {
					this.isSaving = false;
					return;
				}

				this._onCreateSuccess(res, payload.Ranch.Name);
			})
			.catch(() => {
				this.isSaving = false;
			});
	}

	private _onCreateSuccess(res: RanchCreateResponse, ranchName: string): void {
		let navigationExtras: NavigationExtras;
		let ranch: IRanch;

		this.isSaving = false;

		ranch = {
			ActivePlantings: 0,
			CreatedByFullName: null,
			Favorite: false,
			FavoriteId: 0,
			Id: res.Id,
			Name: ranchName,
			RanchAccessState: null,
			RanchId: res.Guid,
			Ranch_External_GUID: res.Guid,
			UserAccessState: null,
			WeatherAPI_Id: res.WeatherAPI_Id
		};

		if (!this._ranchService.ranches.myRanches) {
			this._ranchService.ranches.myRanches = new Array();
		}

		this._ranchService.ranches.myRanches.push(ranch);
		this._ranchService.sortRanchesByName(this._ranchService.ranches.myRanches);
		this._updateService.setUpdatedRanches('ranch created');
		this._ranchService.currentRanchLots = new Array();
		this._ranchService.plantings = new Plantings();
		this._dialogRef.close();

		this._updateService.setCurrentRanchId(res.Guid);
		this._updateService.showRanchSettings = false;

		navigationExtras = {
			queryParams: {
				id: res.Guid
			}
		};

		this._router.navigate(['RanchHome'], navigationExtras);
	}
}
